import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ChequeLeafArchive from '@/components/cheque/ChequeLeafArchive';
import { readChequeBooks, readRegisteredCheques } from '@/lib/chequeBooksStorage';
import { getChequeVisualSettings } from '@/lib/chequeVisualSettings';

function toFaDigits(value: string | number): string {
  return String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
}

const ChequePrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const payload = React.useMemo(() => {
    if (!id) return undefined;
    const c = readRegisteredCheques().find((x) => x.id === id);
    if (!c) return undefined;
    const book = readChequeBooks().find((b) => b.id === c.chequeBookId);
    const visual = getChequeVisualSettings();
    return { c, book, visual };
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  if (!payload) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-100 p-6" dir="rtl">
        <p className="text-slate-700">این چک در حافظهٔ مرورگر یافت نشد.</p>
        <Button asChild variant="outline">
          <Link to="/cheque-books/register">بازگشت به ثبت چک</Link>
        </Button>
      </div>
    );
  }

  const { c, book, visual } = payload;
  const branch = book?.branchName ?? '';

  return (
    <div className="min-h-screen bg-slate-100 p-4 print:bg-white print:p-0" dir="rtl">
      <div className="mx-auto max-w-4xl print:max-w-none">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 print:hidden">
          <h1 className="text-lg font-semibold text-slate-800">چاپ نسخه بایگانی چک</h1>
          <div className="flex gap-2">
            <Button type="button" onClick={handlePrint} className="gap-2">
              <Printer className="h-4 w-4" />
              چاپ
            </Button>
            <Button asChild variant="outline">
              <Link to="/cheque-books/register">بستن</Link>
            </Button>
          </div>
        </div>

        <div id="cheque-archive-print-root" className="print:block">
          <ChequeLeafArchive
            bankAccountLabel={c.bankAccountLabel}
            branchName={branch}
            serie={c.serie}
            leafDisplayFa={toFaDigits(c.leafNumber)}
            fullName={c.fullName}
            amountFormatted={c.amount.toLocaleString('en-US')}
            dueDate={c.dueDate}
            amountInWords={c.amountInWords}
            nationalCode={c.nationalCode}
            description={c.description}
            sealDataUrl={visual.sealDataUrl}
            signatureDataUrl={visual.signatureDataUrl}
            footerNote="نسخه بایگانی الکترونیک — تاریخ صدور مطابق ثبت در سامانه"
          />
        </div>
      </div>
    </div>
  );
};

export default ChequePrintPage;
